import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Link } from "react-router-dom";
import { ArrowLeft, Stethoscope, Mail, Shield, AlertTriangle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useLanguage } from "@/contexts/useLanguage";
import { supabase } from "@/integrations/supabase/client";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { OTPResetPasswordForm } from "./OTPResetPasswordForm";

export const ForgotPasswordForm = () => {
  const [resetMethod, setResetMethod] = useState<'choose' | 'email' | 'otp'>('choose');
  const [isLoading, setIsLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [isEmailSent, setIsEmailSent] = useState(false);
  const { toast } = useToast();
  const { t } = useLanguage();

  // Handle OTP flow
  if (resetMethod === 'otp') {
    return (
      <OTPResetPasswordForm 
        email={email}
        onBack={() => {
          setResetMethod('choose');
          setEmail("");
        }}
      />
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (error) {
        toast({
          title: t("auth.forgotPassword.toast.error.title"),
          description: t("auth.forgotPassword.toast.genericError.desc"),
          variant: "destructive",
        });
        return;
      }

      setIsEmailSent(true);
      toast({
        title: t("auth.forgotPassword.toast.success.title"),
        description: t("auth.forgotPassword.toast.success.desc"),
      });
      
    } catch (error) {
      toast({
        title: t("auth.forgotPassword.toast.genericError.title"),
        description: t("auth.forgotPassword.toast.genericError.desc"),
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Method selection screen
  if (resetMethod === 'choose') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-trust p-4">
        <Card className="w-full max-w-md shadow-elegant border-border/50">
          <CardHeader className="text-center space-y-4">
            <div className="flex justify-center">
              <div className="p-3 bg-primary/10 rounded-full">
                <Stethoscope className="h-8 w-8 text-primary" />
              </div>
            </div>
            <div>
              <CardTitle className="text-2xl font-bold text-foreground">Choose Reset Method</CardTitle>
              <CardDescription className="text-muted-foreground">
                Select how you'd like to reset your password
              </CardDescription>
            </div>
          </CardHeader>
          
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email">Email Address</Label>
              <Input
                id="email"
                type="email"
                placeholder="Enter your email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="transition-all duration-300 focus:shadow-medical"
              />
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-1 gap-3">
                <Button
                  onClick={() => setResetMethod('otp')}
                  variant="outline"
                  className="h-auto p-4 flex flex-col items-start space-y-2 hover:bg-primary/5 hover:border-primary/20"
                  disabled={!email}
                >
                  <div className="flex items-center space-x-2">
                    <Shield className="h-5 w-5 text-primary" />
                    <span className="font-semibold">Verification Code (Recommended)</span>
                  </div>
                  <p className="text-sm text-muted-foreground text-left">
                    Receive a 6-digit code via email. More reliable and secure.
                  </p>
                </Button>

                <Button
                  onClick={() => setResetMethod('email')}
                  variant="outline"
                  className="h-auto p-4 flex flex-col items-start space-y-2 hover:bg-primary/5 hover:border-primary/20"
                  disabled={!email}
                >
                  <div className="flex items-center space-x-2">
                    <Mail className="h-5 w-5 text-primary" />
                    <span className="font-semibold">Reset Link</span>
                  </div>
                  <p className="text-sm text-muted-foreground text-left">
                    Traditional reset link sent to your email.
                  </p>
                </Button>
              </div>

              <Alert>
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  <strong>Having issues with reset links?</strong> Try the verification code method instead. 
                  Reset links can expire quickly or be consumed by email scanners.
                </AlertDescription>
              </Alert>
            </div>

            <div className="text-center">
              <Link
                to="/login"
                className="inline-flex items-center text-sm text-primary hover:text-primary-glow transition-colors"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to login
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Email sent confirmation
  if (isEmailSent) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-trust p-4">
        <Card className="w-full max-w-md shadow-elegant border-border/50">
          <CardHeader className="text-center space-y-4">
            <div className="flex justify-center">
              <div className="p-3 bg-primary/10 rounded-full">
                <Stethoscope className="h-8 w-8 text-primary" />
              </div>
            </div>
            <div>
              <CardTitle className="text-2xl font-bold text-foreground">Check your email</CardTitle>
              <CardDescription className="text-muted-foreground">
                We've sent password reset instructions to {email}
              </CardDescription>
            </div>
          </CardHeader>
          
          <CardContent className="space-y-6">
            <div className="text-center space-y-4">
              <p className="text-sm text-muted-foreground">
                Didn't receive the email? Check your spam folder or try again.
              </p>
              
              <Button
                onClick={() => {
                  setIsEmailSent(false);
                  setEmail("");
                  setResetMethod('choose');
                }}
                variant="outline"
                className="w-full"
              >
                Try different method
              </Button>
            </div>

            <div className="text-center">
              <Link
                to="/login"
                className="inline-flex items-center text-sm text-primary hover:text-primary-glow transition-colors"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to login
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Traditional email reset form
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-trust p-4">
      <Card className="w-full max-w-md shadow-elegant border-border/50">
        <CardHeader className="text-center space-y-4">
          <div className="flex justify-center">
            <div className="p-3 bg-primary/10 rounded-full">
              <Stethoscope className="h-8 w-8 text-primary" />
            </div>
          </div>
          <div>
            <CardTitle className="text-2xl font-bold text-foreground">Reset your password</CardTitle>
            <CardDescription className="text-muted-foreground">
              Enter your email address and we'll send you a link to reset your password
            </CardDescription>
          </div>
        </CardHeader>
        
        <CardContent className="space-y-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">{t("auth.email")}</Label>
              <Input
                id="email"
                type="email"
                placeholder={t("auth.emailPlaceholder")}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="transition-all duration-300 focus:shadow-medical"
              />
            </div>

            <Button
              type="submit"
              variant="hero"
              size="lg"
              className="w-full"
              disabled={isLoading}
            >
              {isLoading ? t("auth.forgotPassword.sending") : t("auth.forgotPassword.sendButton")}
            </Button>
          </form>

          <div className="text-center space-y-2">
            <Button
              variant="ghost"
              onClick={() => setResetMethod('choose')}
              className="text-sm"
            >
              Choose different reset method
            </Button>
            
            <Link
              to="/login"
              className="inline-flex items-center text-sm text-primary hover:text-primary-glow transition-colors"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to login
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};